/**
 * EditInvoiceTotalsModal Component
 *
 * Modal shell around `InvoiceTotalsForm`. Owns dialog chrome, save lifecycle
 * (`saving` flag, save-error capture). The form itself handles the inputs +
 * derived VAT amount + breakdown-mismatch warning via `useInvoiceTotalsFormState`.
 *
 * Uses `useTranslation('modules.modals.editInvoiceTotals')` for chrome labels.
 */
import * as React from 'react';
import { useState } from 'react';
import { useSuggestedExchangeRate } from '../forms/invoice-totals/hooks';
import { ZERO, type Decimal } from '@insio/decimal';
import type { IDocumentExchangeRate } from '@insio/shared-types';
import { resolveExchangeRateValue } from '../forms/invoice-totals/resolve-exchange-rate-value';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Icon } from '../../components/icons/Icon';
import { useEnterSubmit } from '../../hooks';
import { useTranslation } from '../../providers/UIProvider';
import { useInvoiceIsVatDocument } from '../../providers/InvoiceProvider';
import { getTestId, type TestIdProps } from '../../utils';
import {
  InvoiceTotalsForm,
  type InvoiceTotalsFormData,
  type InvoiceTotalsExpected
} from '../forms/invoice-totals/InvoiceTotalsForm';
import { useInvoiceTotalsFormState } from '../forms/invoice-totals/hooks';
import type { InvoiceTotalsPaymentStatus } from '../forms/invoice-totals/hooks';
import type { ValidationIssue } from '../../types';

// Re-export types so existing imports from `EditInvoiceTotalsModal` keep working.
export {
  InvoiceTotalsForm,
  type InvoiceTotalsFormData,
  type InvoiceTotalsExpected
} from '../forms/invoice-totals/InvoiceTotalsForm';
export type { InvoiceTotalsPaymentStatus } from '../forms/invoice-totals/hooks';

export interface EditInvoiceTotalsModalProps extends TestIdProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: InvoiceTotalsFormData) => Promise<void>;
  currentTotalNoVat: Decimal;
  currentTotalVatAmount: Decimal;
  currentTotalInclVat: Decimal;
  /** The invoice's paid status, shown read-only. */
  currentPaymentStatus?: InvoiceTotalsPaymentStatus | null;
  /** The invoice's paid amount, shown read-only. */
  currentPaidAmount?: Decimal | null;
  currentAccountingExchangeRate?: IDocumentExchangeRate | null;
  currentRoundingAmount?: Decimal | null;
  currentAccountingAmount?: Decimal | null;
  currentAccountingAmountNoVat?: Decimal | null;
  currentAccountingTotalVatAmount?: Decimal | null;
  currentAccountingCurrencyCode?: string | null;
  currencyCode?: string | null;
  accountingCurrencyCode?: string | null;
  /** Totals summed from the VAT summary — enables the "fill from VAT summary" button. */
  expectedTotalsFromVatSummary?: InvoiceTotalsExpected | null;
  /** Totals summed from the line items — enables the "fill from line items" button. */
  expectedTotalsFromLines?: InvoiceTotalsExpected | null;
  /**
   * Show the accounting-exchange-rate input. Defaults to `false`; only
   * meaningful when the invoice currency differs from the accounting
   * currency. Forwarded directly to `InvoiceTotalsForm`.
   */
  showAccountingExchangeRate?: boolean;
  /**
   * Validation issues to surface inside the form. `totalAmountNoVat`,
   * `totalAmountInclVat`, `accountingExchangeRate` and `roundingAmount` paths
   * annotate their inputs; empty-path issues render as a banner above the inputs.
   */
  errors?: ValidationIssue[];
}

export const EditInvoiceTotalsModal: React.FC<EditInvoiceTotalsModalProps> = ({
  open,
  onClose,
  onSave,
  currentTotalNoVat,
  currentTotalVatAmount,
  currentTotalInclVat,
  currentPaymentStatus,
  currentPaidAmount,
  currentAccountingExchangeRate,
  currentRoundingAmount,
  currentAccountingAmount,
  currentAccountingAmountNoVat,
  currentAccountingTotalVatAmount,
  currentAccountingCurrencyCode,
  currencyCode,
  accountingCurrencyCode,
  expectedTotalsFromVatSummary,
  expectedTotalsFromLines,
  showAccountingExchangeRate = false,
  errors,
  testId,
  id
}) => {
  const t = useTranslation('modules.modals.editInvoiceTotals');
  const isVatDocument = useInvoiceIsVatDocument();
  const state = useInvoiceTotalsFormState({
    open,
    currentTotalNoVat,
    currentTotalVatAmount,
    currentTotalInclVat,
    currentAccountingExchangeRate: currentAccountingExchangeRate?.rate ?? null,
    currentRoundingAmount,
    currentAccountingAmount,
    currentAccountingAmountNoVat,
    currentAccountingTotalVatAmount,
    currentAccountingCurrencyCode: currentAccountingCurrencyCode ?? accountingCurrencyCode ?? null,
    documentCurrencyCode: currencyCode ?? null
  });
  const [saving, setSaving] = useState(false);
  const suggestedAccountingExchangeRate = useSuggestedExchangeRate(
    state.accountingCurrencyCode,
    state.currenciesSame,
    open
  );

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    const {
      finalNoVat,
      finalVatAmount,
      finalInclVat,
      finalAccountingExchangeRate,
      finalRoundingAmount,
      finalAccountingAmount,
      finalAccountingAmountNoVat,
      finalAccountingTotalVatAmount,
      finalAccountingCurrencyCode
    } = state.flushAndGet();
    setSaving(true);
    try {
      // Non-VAT document: only the net total is editable, so mirror it into the
      // gross total and zero the VAT amounts to keep stored values consistent.
      await onSave({
        totalAmountNoVat: finalNoVat,
        totalVatAmount: isVatDocument ? finalVatAmount : ZERO,
        totalAmountInclVat: isVatDocument ? finalInclVat : finalNoVat,
        accountingExchangeRate: resolveExchangeRateValue(
          finalAccountingExchangeRate,
          currentAccountingExchangeRate,
          suggestedAccountingExchangeRate
        ),
        roundingAmount: finalRoundingAmount,
        accountingAmount: isVatDocument ? finalAccountingAmount : finalAccountingAmountNoVat,
        accountingAmountNoVat: finalAccountingAmountNoVat,
        accountingTotalVatAmount: isVatDocument ? finalAccountingTotalVatAmount : ZERO,
        accountingCurrencyCode: finalAccountingCurrencyCode
      });
      onClose();
    } catch (err) {
      state.setError(err instanceof Error ? err.message : t('errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnterKeyDown = useEnterSubmit(handleSave);

  return (
    <Modal open={open} onClose={handleClose} size='md' testId={getTestId({ testId, id })}>
      <Modal.Header
        label={t('title')}
        description={t('description')}
        onClose={handleClose}
        variant='white'
        icon={<Icon name='coins' className='w-6 h-6 text-primary-base' />}
      />

      <Modal.Content className='p-0 bg-background-base' size='full'>
        <div className='py-sm px-sm' onKeyDown={handleEnterKeyDown}>
          <InvoiceTotalsForm
            state={state}
            currencyCode={currencyCode}
            accountingCurrencyCode={accountingCurrencyCode}
            expectedTotalsFromVatSummary={expectedTotalsFromVatSummary}
            expectedTotalsFromLines={expectedTotalsFromLines}
            paymentStatus={currentPaymentStatus}
            paidAmount={currentPaidAmount}
            showAccountingExchangeRate={showAccountingExchangeRate}
            suggestedAccountingExchangeRate={suggestedAccountingExchangeRate}
            disabled={saving}
            errors={errors}
          />
        </div>
      </Modal.Content>

      <Modal.Footer variant='white'>
        <Modal.Action className='flex-row justify-end'>
          <Button variant='ghost' size='md' onClick={handleClose} disabled={saving}>
            {t('actions.cancel')}
          </Button>
          <Button variant='primary' size='md' onClick={handleSave} disabled={saving}>
            {saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
};

EditInvoiceTotalsModal.displayName = 'EditInvoiceTotalsModal';
